/**
 * /dashboard/payroll/statutory — G_STAT statutory register & return
 * export hub.
 *
 * Surfaces every statutory CSV that can be generated from locked payroll
 * snapshots (Companies Act 2013 §128(1) anchor — only locked runs are
 * exportable). The page is HR-admin only (same RBAC as every
 * /api/v1/payroll/* route server-side).
 *
 * Tile inventory (7 wired, 1 pointer, 1 per-employee forms tile):
 *   1. EPFO ECR          — a pointer to Tax & PF filings, where the
 *                          uploadable "#~#" ECR is generated per month. The
 *                          CSV export this tile offered (EPS 0.00, wages
 *                          with paise) disagreed with that file (2026-10
 *                          re-test payroll #11), so it is no longer offered.
 *   2. ESI half-yearly   — H1/H2 window    (ESI Act §39)
 *   3. PT challan        — per locked run  (state PT Acts)
 *   4. Bonus Form D      — FY              (Payment of Bonus Act §26)
 *   5. S&E annual return — calendar year   (state S&E Acts)
 *   6. ER Form D         — quarter         (ER Act 1976 §8)
 *   7. LWF biannual      — H1/H2 window    (state LWF Acts)
 *   8. Maternity §11A    — FY              (Maternity Benefit Act §11A)
 *   9. Form 11 (EPF)     — per employee    (StatutoryFormsTile: pick an
 *  10. Form F (Gratuity) — per employee     employee, view / fill in / print)
 *
 * Pattern mirrors app.$tenantSlug.accounting.returns.tsx exactly.
 * No generate/POST flow here — all are direct GET CSV downloads once the
 * period params are set.
 */

import { REGISTERS_AND_RETURNS, TAX_AND_PF_FILINGS } from "@/lib/payroll-pages";
import { useState } from "react";
import {
  createFileRoute,
  Link,
  useParams,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Download,
  FileText,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { saveBlobAs } from "@/lib/save-blob";
import { extensionForType, filenameStem } from "@/lib/download-filename";
import { payrollApi, toApiError } from "@/lib/api";
import { usePayrollRuns } from "@/hooks/use-payroll";
import { periodFilingRuns, periodNetPaise, type PeriodFilingRun } from "@/lib/payroll-order";
import { formatPeriod, type PayrollRun, type UANMissingEmployee } from "@/types/payroll";
import { uanMissingFromError } from "@/lib/payroll-errors";
import { UANMissingAlert } from "@/components/payroll/employee-issue-alerts";
import { formatPaise } from "@/lib/expense-format";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { PeriodWarningsBanner } from "@/components/payroll/period-warnings-banner";
import { StatutoryFormsTile } from "@/components/payroll/statutory-forms-tile";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/payroll/statutory",
)({
  component: StatutoryRoute,
});

// ---------------------------------------------------------------------------
// Period helpers
// ---------------------------------------------------------------------------

function currentYear(): number {
  return new Date().getUTCFullYear();
}

function currentMonth(): number {
  return new Date().getUTCMonth() + 1;
}

/** Current Indian FY year (Apr start). */
function currentFYYear(): number {
  const m = currentMonth();
  return m >= 4 ? currentYear() : currentYear() - 1;
}

/** Previous calendar-half (for LWF biannual — file AFTER half ends). */
function prevCalendarHalf(): { half: 1 | 2; year: number } {
  if (currentMonth() <= 6) {
    // We're in H1 → previous was H2 of last year
    return { half: 2, year: currentYear() - 1 };
  }
  return { half: 1, year: currentYear() };
}

/** ESI statutory half — H1=Apr-Sep, H2=Oct-Mar of FY. */
function currentESIHalf(): 1 | 2 {
  const m = currentMonth();
  return m >= 4 && m <= 9 ? 1 : 2;
}

function yearRange(): number[] {
  const fy = currentFYYear();
  const years: number[] = [];
  for (let y = fy; y >= 2021; y--) years.push(y);
  return years;
}

function calendarYearRange(): number[] {
  const y = currentYear();
  const years: number[] = [];
  for (let i = y; i >= 2021; i--) years.push(i);
  return years;
}

const QUARTER_LABELS: Record<number, string> = {
  1: "Q1 (Jan–Mar)", 2: "Q2 (Apr–Jun)", 3: "Q3 (Jul–Sep)", 4: "Q4 (Oct–Dec)",
};

// ESI windows per ESI Act §39: H1 = Apr 01 – Sep 30; H2 = Oct 01 – Mar 31.
function esiWindow(fyYear: number, half: 1 | 2): { from: string; to: string } {
  if (half === 1) {
    return { from: `${fyYear}-04-01`, to: `${fyYear}-09-30` };
  }
  // H2: Oct of fyYear to Mar of fyYear+1
  return { from: `${fyYear}-10-01`, to: `${fyYear + 1}-03-31` };
}

// ---------------------------------------------------------------------------
// Download builders — each returns an async () => Promise<Blob> calling the
// authed blob helper in lib/api/payroll.ts. We no longer use raw
// <a href download> links: the common 422 (no_locked_runs / incomplete_fy
// / run_not_locked) used to render its {"error":...} envelope as a raw
// JSON page in a new tab. Fetching the blob via the shared api client lets
// blobErrorToApi rehydrate that envelope so the tile can toast the
// sentinel message (B-A9). The server's Content-Disposition sets the
// filename — we let it win (empty download name).
// ---------------------------------------------------------------------------

// FY label helper — Indian FY runs Apr→Mar, e.g. fyYear=2025 → "2025-26".
function fyLabel(fyYear: number): string {
  return `${fyYear}-${String((fyYear + 1) % 100).padStart(2, "0")}`;
}

function esiDownload(fyYear: number, half: 1 | 2): () => Promise<Blob> {
  const { from, to } = esiWindow(fyYear, half);
  return () => payrollApi.downloadESIReturn(from, to);
}

function ptChallanDownload(runId: number): () => Promise<Blob> {
  return () => payrollApi.downloadPTChallan(runId);
}

function bonusFormDDownload(fyYear: number): () => Promise<Blob> {
  return () => payrollApi.downloadBonusFormD(`${fyYear}-04-01`);
}

function seReturnDownload(year: number): () => Promise<Blob> {
  // GET /api/v1/payroll/se-return.csv?year=YYYY — the S&E return is for a
  // calendar year (re-test payroll #4: the tile called a route that never
  // existed).
  return () => payrollApi.downloadSEReturn(year);
}

// G_STAT (Wave F) — backend handlers now wired. All return text/csv; the
// browser handles the save via the server's Content-Disposition filename.

function erRegisterDownload(fyYear: number, _quarter: number): () => Promise<Blob> {
  // Equal Remuneration Form D is an ANNUAL return (ER Act 1976 §8),
  // not quarterly. The quarter param remains in the signature for
  // back-compat with the existing form but is ignored.
  return () => payrollApi.downloadERRegister(fyLabel(fyYear));
}

function lwfDownload(year: number, half: 1 | 2): () => Promise<Blob> {
  return () => payrollApi.downloadLWFReturn(year, half);
}

function maternityReturnDownload(fyYear: number): () => Promise<Blob> {
  return () => payrollApi.downloadMaternityReturn(fyLabel(fyYear));
}

/**
 * Save a statutory export: under the server's Content-Disposition filename
 * (the API helpers attach it — a blob: URL has no headers), else
 * "<tile title>.<ext>" from the response type. Audit Q27: the nameless
 * link saved every file as <uuid>.csv.
 */
function saveExport(blob: Blob, title: string) {
  saveBlobAs(blob, `${filenameStem(title)}${extensionForType(blob.type) || ".csv"}`);
}

/**
 * Human label for a locked month in the run picker, e.g.
 * "January 2026 — net ₹4,52,300.00": the regular run's net plus the
 * corrections the entry says it includes (periodNetPaise — re-test payroll
 * #12: it summed the regular run alone).
 */
function lockedRunOptionLabel(entry: PeriodFilingRun<PayrollRun>, runs: readonly PayrollRun[]): string {
  const { run } = entry;
  return `${formatPeriod(run.period_year, run.period_month)} — net ${formatPaise(periodNetPaise(entry, runs))}`;
}

// ---------------------------------------------------------------------------
// Shared tile shape
// ---------------------------------------------------------------------------

interface WiredTileProps {
  title: string;
  description: string;
  cadence: string;
  statutoryNote: string;
  /**
   * Fetches the export as an authed blob. A non-2xx (e.g. 422
   * no_locked_runs / incomplete_fy / run_not_locked) rejects with the
   * rehydrated error envelope so the tile toasts a friendly message
   * instead of opening a raw-JSON tab (B-A9).
   */
  onDownload: () => Promise<Blob>;
  /** Workspace slug — set on tiles whose refusal names employees (the ECR's
   *  422 uan_missing), so the alert links each one to their record. */
  tenantSlug?: string;
}

// ---------------------------------------------------------------------------
// WiredTile — direct GET CSV download link
// ---------------------------------------------------------------------------

function WiredTile({
  title,
  description,
  cadence,
  statutoryNote,
  onDownload,
  tenantSlug,
}: Readonly<WiredTileProps>) {
  const [downloading, setDownloading] = useState(false);
  // F199: the ECR's 422 uan_missing names every PF member without a UAN —
  // listed in the tile (a toast showed only the count).
  const [uanMissing, setUANMissing] = useState<UANMissingEmployee[] | null>(null);

  async function handleDownload() {
    setDownloading(true);
    setUANMissing(null);
    try {
      const blob = await onDownload();
      saveExport(blob, title);
      toast.success(`${title} downloaded.`); // QA payroll #17: no confirmation before
    } catch (err) {
      const missing = uanMissingFromError(err);
      if (missing && tenantSlug) {
        setUANMissing(missing);
        return;
      }
      // A 422 sentinel (no_locked_runs / incomplete_fy / run_not_locked)
      // now surfaces as a friendly toast — blobErrorToApi rehydrated the
      // envelope in the api layer — instead of a raw-JSON tab (B-A9).
      toast.error(toApiError(err).message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Card data-testid={`statutory-tile-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
              <FileText className="h-4 w-4" />
            </span>
            <div>
              <CardTitle className="text-sm font-semibold">{title}</CardTitle>
              <CardDescription className="text-xs">{description}</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="shrink-0 text-xs capitalize">
            {cadence}
          </Badge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{statutoryNote}</p>
      </CardHeader>
      <CardContent>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-1.5"
          disabled={downloading}
          onClick={handleDownload}
        >
          {downloading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          {downloading ? "Downloading…" : "Download CSV"}
        </Button>
        {uanMissing && tenantSlug && (
          <div className="mt-3">
            <UANMissingAlert employees={uanMissing} tenantSlug={tenantSlug} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main route
// ---------------------------------------------------------------------------

function StatutoryRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };

  // Shared period state — each tile reads its own relevant slice.
  const [fyYear, setFYYear] = useState<number>(currentFYYear);
  const [esiHalf, setESIHalf] = useState<1 | 2>(currentESIHalf);
  const [esiFYYear, setESIFYYear] = useState<number>(currentFYYear);
  const [calYear, setCalYear] = useState<number>(currentYear);
  const [erYear, setERYear] = useState<number>(currentYear);
  const [erQuarter, setERQuarter] = useState<number>(1);
  const [lwfYear, setLWFYear] = useState<number>(prevCalendarHalf().year);
  const [lwfHalf, setLWFHalf] = useState<1 | 2>(prevCalendarHalf().half);
  const [maternityFYYear, setMaternityFYYear] = useState<number>(currentFYYear);

  // The PT challan needs a locked run (the ECR moved to Tax & PF filings).
  const [ptRunId, setPTRunId] = useState<string>("");
  const ptIdTrimmed = ptRunId.trim();

  // Locked-run picker source — the PT challan export is only valid against
  // a LOCKED run (Companies Act 2013 §128(1)). A Select of actual locked
  // runs so HR can't mistype an id or point at an unlocked run.
  const runsQuery = usePayrollRuns();
  const allRuns = runsQuery.data?.items ?? [];
  // One entry per MONTH (QA payroll #20): the month's locked regular run,
  // its locked corrections folded in by the server — never the correction
  // as a second entry (both downloaded the same consolidated month).
  const lockedRuns = periodFilingRuns(allRuns);

  function selectRun(value: string) {
    setPTRunId(value);
  }

  return (
    <PayrollModuleShell
      title={REGISTERS_AND_RETURNS}
      description="Download statutory registers and returns as CSV files for portal upload. All exports require locked payroll runs (Companies Act 2013 §128(1))."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/payroll" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Payroll
          </Link>
        </Button>
      }
    >
      {/* Wave 2 — months corrected with a second full run (pre-correction-
          runs) are flagged; every export below uses the latest run only, and
          a month's correction runs are folded into its figures. */}
      <div className="mb-4">
        <PeriodWarningsBanner tenantSlug={tenantSlug} />
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Period selector strip                                              */}
      {/* ----------------------------------------------------------------- */}
      <Card>
        <CardContent className="py-4">
          <div className="grid gap-4 sm:flex sm:flex-wrap sm:items-end">
            {/* FY year — Bonus Form D + Maternity §11A */}
            <div className="grid gap-1.5">
              <Label htmlFor="stat-fy-year">FY Year (Bonus / Maternity)</Label>
              <Select
                value={String(fyYear)}
                onValueChange={(v) => {
                  setFYYear(Number(v));
                  setMaternityFYYear(Number(v));
                }}
              >
                <SelectTrigger id="stat-fy-year" className="w-full sm:w-40" data-testid="stat-fy-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearRange().map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      FY {y}–{String(y + 1).slice(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Calendar year — S&E annual */}
            <div className="grid gap-1.5">
              <Label htmlFor="stat-cal-year">Calendar Year (S&amp;E)</Label>
              <Select
                value={String(calYear)}
                onValueChange={(v) => setCalYear(Number(v))}
              >
                <SelectTrigger id="stat-cal-year" className="w-full sm:w-36" data-testid="stat-cal-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {calendarYearRange().map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ER quarter */}
            <div className="grid gap-1.5">
              <Label htmlFor="stat-er-year">ER Quarter</Label>
              <div className="grid grid-cols-1 gap-2 min-[360px]:grid-cols-2 sm:flex">
                <Select
                  value={String(erYear)}
                  onValueChange={(v) => setERYear(Number(v))}
                >
                  <SelectTrigger id="stat-er-year" className="w-full sm:w-28" data-testid="stat-er-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {calendarYearRange().map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={String(erQuarter)}
                  onValueChange={(v) => setERQuarter(Number(v))}
                >
                  <SelectTrigger aria-label="Quarter" id="stat-er-quarter" className="w-full sm:w-36" data-testid="stat-er-quarter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4].map((q) => (
                      <SelectItem key={q} value={String(q)}>
                        {QUARTER_LABELS[q]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ESI half-year — FY-scoped */}
            <div className="grid gap-1.5">
              <Label htmlFor="stat-esi-fy">ESI Half-Year</Label>
              <div className="grid grid-cols-1 gap-2 min-[360px]:grid-cols-2 sm:flex">
                <Select
                  value={String(esiFYYear)}
                  onValueChange={(v) => setESIFYYear(Number(v))}
                >
                  <SelectTrigger id="stat-esi-fy" className="w-full sm:w-32" data-testid="stat-esi-fy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearRange().map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        FY {y}–{String(y + 1).slice(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={String(esiHalf)}
                  onValueChange={(v) => setESIHalf(Number(v) as 1 | 2)}
                >
                  <SelectTrigger aria-label="Half" id="stat-esi-half" className="w-full sm:w-36" data-testid="stat-esi-half">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">H1 (Apr–Sep)</SelectItem>
                    <SelectItem value="2">H2 (Oct–Mar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* LWF biannual — calendar-year-scoped */}
            <div className="grid gap-1.5">
              <Label htmlFor="stat-lwf-year">LWF Half-Year</Label>
              <div className="grid grid-cols-1 gap-2 min-[360px]:grid-cols-2 sm:flex">
                <Select
                  value={String(lwfYear)}
                  onValueChange={(v) => setLWFYear(Number(v))}
                >
                  <SelectTrigger id="stat-lwf-year" className="w-full sm:w-28" data-testid="stat-lwf-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {calendarYearRange().map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={String(lwfHalf)}
                  onValueChange={(v) => setLWFHalf(Number(v) as 1 | 2)}
                >
                  <SelectTrigger aria-label="Half" id="stat-lwf-half" className="w-full sm:w-36" data-testid="stat-lwf-half">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">H1 (Jan–Jun)</SelectItem>
                    <SelectItem value="2">H2 (Jul–Dec)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Per-run picker — ECR + PT require a LOCKED run. The Select is
              populated from the payroll runs list filtered to locked runs;
              its value is the run id the export builders already consume. */}
          <div className="mt-4 grid gap-4 border-t pt-4 sm:flex sm:flex-wrap sm:items-start">
            <div className="grid gap-1.5">
              <Label htmlFor="stat-run-picker">
                PT Challan — Locked Month
              </Label>
              <p className="text-xs text-muted-foreground">
                Choose a locked payroll month. Only locked payroll can be
                exported as a PT challan (Companies Act 2013 §128(1)).
              </p>
            </div>
            <div className="grid gap-1.5">
              {/* Distinct keys: the loading placeholder (no value) and the
                  run picker (controlled) sit in the same slot, so without
                  them React reuses the Select and Radix warns "Select is
                  changing from uncontrolled to controlled". */}
              {runsQuery.isLoading ? (
                <Select key="loading" disabled>
                  <SelectTrigger
                    id="stat-run-picker"
                    className="w-full sm:w-72"
                    data-testid="stat-run-picker"
                  >
                    <SelectValue placeholder="Loading runs…" />
                  </SelectTrigger>
                  <SelectContent />
                </Select>
              ) : lockedRuns.length === 0 ? (
                <>
                  <Select disabled>
                    <SelectTrigger
                      id="stat-run-picker"
                      className="w-full sm:w-72"
                      data-testid="stat-run-picker"
                    >
                      <SelectValue placeholder="No locked runs" />
                    </SelectTrigger>
                    <SelectContent />
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Lock a payroll run first.
                  </p>
                </>
              ) : (
                <Select key="runs" value={ptRunId} onValueChange={selectRun}>
                  <SelectTrigger
                    id="stat-run-picker"
                    className="w-full sm:w-72"
                    data-testid="stat-run-picker"
                  >
                    <SelectValue placeholder="Select a locked run" />
                  </SelectTrigger>
                  <SelectContent>
                    {lockedRuns.map((entry) => (
                      <SelectItem key={entry.run.id} value={String(entry.run.id)}>
                        {lockedRunOptionLabel(entry, allRuns)}
                        {entry.correctionIds.length > 0
                          ? ` · Includes Correction ${entry.correctionIds.map((id) => `#${id}`).join(", ")}`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Tile grid                                                          */}
      {/* ----------------------------------------------------------------- */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">

        {/* 1. EPFO ECR — generated under Tax & PF filings (the "#~#"
            upload file, one per month). This page's CSV export disagreed
            with it (EPS 0.00, wages with paise — re-test payroll #11). */}
        <Card data-testid="statutory-tile-epfo-ecr">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                  <FileText className="h-4 w-4" />
                </span>
                <div>
                  <CardTitle className="text-sm font-semibold">EPFO ECR — Monthly Contribution</CardTitle>
                  <CardDescription className="text-xs">Generated under {TAX_AND_PF_FILINGS}</CardDescription>
                </div>
              </div>
              <Badge variant="outline" className="shrink-0 text-xs">Monthly</Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              EPF Act 1952 §6 — the Electronic Challan cum Return you upload on the EPFO Unified Portal. Generate
              each month&apos;s file under {TAX_AND_PF_FILINGS}: it is the exact upload format, with the EPS share
              and whole-rupee wages EPFO expects.
            </p>
          </CardHeader>
          <CardContent>
            <Button asChild size="sm" variant="outline" className="w-full">
              <Link to="/app/$tenantSlug/payroll/filings" params={{ tenantSlug }}>
                Open {TAX_AND_PF_FILINGS}
              </Link>
            </Button>
          </CardContent>
        </Card>

        {/* 2. ESI Half-Yearly Return */}
        <WiredTile
          title="ESI Half-Yearly Return"
          description={`FY ${esiFYYear}–${String(esiFYYear + 1).slice(2)}, ${esiHalf === 1 ? "H1 (Apr–Sep)" : "H2 (Oct–Mar)"}`}
          cadence="Half-Yearly"
          statutoryNote="ESI Act 1948 §39 — Aggregates every locked run in the half; refuses if zero locked runs found."
          onDownload={esiDownload(esiFYYear, esiHalf)}
        />

        {/* 3. PT Challan */}
        {ptIdTrimmed ? (
          <WiredTile
            title="Professional Tax Challan"
            description={`Payroll run #${ptIdTrimmed}`}
            cadence="Monthly"
            statutoryNote="State Professional Tax Acts — each employee at their state's slab, capped at ₹2,500 a year (Constitution Art. 276); one row per state."
            onDownload={ptChallanDownload(Number(ptIdTrimmed))}
          />
        ) : (
          <Card data-testid="statutory-tile-pt-challan-pending">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                    <FileText className="h-4 w-4" />
                  </span>
                  <div>
                    <CardTitle className="text-sm font-semibold">Professional Tax Challan</CardTitle>
                    <CardDescription className="text-xs">Select a locked run above to enable</CardDescription>
                  </div>
                </div>
                <Badge variant="outline" className="shrink-0 text-xs">Monthly</Badge>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                State Professional Tax Acts — each employee at their state&apos;s slab, capped at ₹2,500 a year (Constitution Art. 276); one row per state.
              </p>
            </CardHeader>
            <CardContent>
              <Button size="sm" variant="outline" className="w-full" disabled>
                Select a Locked Run to Download
              </Button>
            </CardContent>
          </Card>
        )}

        {/* 4. Bonus Form D */}
        <WiredTile
          title="Bonus Form D — Annual Return"
          description={`FY ${fyYear}–${String(fyYear + 1).slice(2)} (Apr ${fyYear} – Mar ${fyYear + 1})`}
          cadence="Annual (FY)"
          statutoryNote="Payment of Bonus Act 1965 §26 + Rules 1975 Rule 5 — all 12 months must be locked."
          onDownload={bonusFormDDownload(fyYear)}
        />

        {/* 4b. Statutory bonus — a link only: the calculator is its own
            three-step page (docs/superpowers/specs/2026-10-21-payroll-
            statutory-accuracy.md §4). */}
        <Card data-testid="statutory-tile-statutory-bonus">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                  <FileText className="h-4 w-4" />
                </span>
                <div>
                  <CardTitle className="text-sm font-semibold">Statutory Bonus</CardTitle>
                  <CardDescription className="text-xs">Calculate, download and add to payroll</CardDescription>
                </div>
              </div>
              <Badge variant="outline" className="shrink-0 text-xs">Annual (FY)</Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Code on Wages 2019 — bonus for people earning up to ₹21,000 a month, worked out on their wages for the
              year. Due within 8 months of the year end.
            </p>
          </CardHeader>
          <CardContent>
            <Button asChild size="sm" variant="outline" className="w-full">
              <Link to="/app/$tenantSlug/payroll/statutory-bonus" params={{ tenantSlug }}>
                Open Statutory Bonus
              </Link>
            </Button>
          </CardContent>
        </Card>

        {/* 5. S&E Annual Register */}
        <WiredTile
          title="Shops &amp; Establishments Annual Register"
          description={`Calendar year ${calYear}`}
          cadence="Annual"
          statutoryNote="State Shops &amp; Establishments Acts — all 12 calendar months must have locked runs."
          onDownload={seReturnDownload(calYear)}
        />

        {/* 6. Equal Remuneration Form D */}
        <WiredTile
          title="Equal Remuneration Register (Form D)"
          description={`${erYear} ${QUARTER_LABELS[erQuarter]}`}
          cadence="Quarterly"
          statutoryNote="Equal Remuneration Act 1976 §8 + Rules 1976 Rule 6 — all 3 months of the quarter must be locked."
          onDownload={erRegisterDownload(erYear, erQuarter)}
        />

        {/* 7. LWF Biannual */}
        <WiredTile
          title="LWF Biannual Contribution"
          description={`${lwfYear}, ${lwfHalf === 1 ? "H1 (Jan–Jun)" : "H2 (Jul–Dec)"}`}
          cadence="Biannual"
          statutoryNote="State Labour Welfare Fund Acts — January–June or July–December of one year. Rates are built in for Tamil Nadu, Karnataka, Maharashtra, West Bengal and Gujarat."
          onDownload={lwfDownload(lwfYear, lwfHalf)}
        />

        {/* 8. Maternity §11A Annual Return */}
        <WiredTile
          title="Maternity Benefit §11A Annual Return"
          description={`FY ${maternityFYYear}–${String(maternityFYYear + 1).slice(2)}`}
          cadence="Annual (FY)"
          statutoryNote="Maternity Benefit Act 1961 §11A — per approved ML instance overlapping the FY; female employees only (§5(2))."
          onDownload={maternityReturnDownload(maternityFYYear)}
        />

        {/* 9–10. Form 11 + Form F — per employee (wave B) */}
        <StatutoryFormsTile />
      </div>
    </PayrollModuleShell>
  );
}
